import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { login } from "../../slices/authSlice";
import logo from "../../assets/media/photos/logo1.png";
import logo2 from "../../assets/media/photos/piadar_login_logo.png";
import uno from "../../assets/media/photos/uripion-union2.png";
import unido from "../../assets/media/photos/UNIDO.png";
import { SubmitButton, ThinTextField } from "../common/GeneralComponent";
import { Checkbox } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import bg from "../../assets/images/bg.jpg";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, user: authUser } = useSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [firstPermittedRoute, setFirstPermittedRoute] = useState("/");

  // Validation schema
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .trim()
      .email("Invalid email address")
      .required("Email is required")
      .test("no-spaces", "Spaces are not allowed", (v) => !v || !/\s/.test(v)),
    password: Yup.string()
      .required("Password is required")
      .test("no-spaces", "Spaces are not allowed", (v) => !v || !/\s/.test(v)),
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    register,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { email: "", password: "" },
    mode: "onSubmit",
  });

  // Register fields (we'll control with setValue + watch)
  useEffect(() => {
    register("email");
    register("password");
  }, [register]);

  // Helpers to keep inputs space-free
  const handleInputChange = (fieldName, value) => {
    const noSpaces = value.replace(/\s/g, "");
    setValue(fieldName, noSpaces, { shouldValidate: false, shouldDirty: true });
  };
  const handleKeyDown = (e) => {
    if (e.key === " ") e.preventDefault();
  };

  const emailVal = watch("email");
  const passwordVal = watch("password");

  useEffect(() => {
    if (authUser?.user_permissions) {
      const getFirstSidebarRoute = () => {
        const userManagementOptions = authUser.user_permissions.includes(
          "group-view",
        )
          ? [{ link: "/Dashboard" }]
          : [];
        const metadataOptions = authUser.user_permissions.includes(
          "province-fetch",
        )
          ? [{ link: "/provinces" }]
          : [];
        const nonformalEducationOptions = authUser.user_permissions.includes(
          "non-formal-detail-view",
        )
          ? [{ link: "/non_formal_detailed" }]
          : [];
        const educationFoundationOptions = authUser.user_permissions.includes(
          "education-foundation-view",
        )
          ? [{ link: "/ef_detailed" }]
          : [];
        const PrivateSchoolsOptions = authUser.user_permissions.includes(
          "private-school-summarized-view",
        )
          ? [{ link: "/ps_summarized" }]
          : [];
        const CollegePublicPrivateOptions = authUser.user_permissions.includes(
          "public-private-colleges-detailed-view",
        )
          ? [{ link: "/collegePublicPrivateDetailed" }]
          : [];
        const OtherPublicOptions = authUser.user_permissions.includes(
          "other-public-fetch",
        )
          ? [{ link: "/other_summarized" }]
          : [];
        const specialeducationOptions = authUser.user_permissions.includes(
          "special-education-detail-fetch",
        )
          ? [{ link: "/special_detailed" }]
          : [];
        const teacherTrainingOptions = authUser.user_permissions.includes(
          "teacherTraining-fetch",
        )
          ? [{ link: "/teachertraining" }]
          : [];
        const DeeniMadarisOptions = authUser.user_permissions.includes(
          "deeni-madaris-summarized-view",
        )
          ? [{ link: "/deeni_madaris_summarized" }]
          : [];

        const menuItemsConfig = [
          ...(userManagementOptions.length
            ? [{ options: userManagementOptions }]
            : []),
          ...(authUser.user_permissions.includes("lookup-view")
            ? [{ options: metadataOptions }]
            : []),
          ...(nonformalEducationOptions.length
            ? [{ options: nonformalEducationOptions }]
            : []),
          ...(educationFoundationOptions.length
            ? [{ options: educationFoundationOptions }]
            : []),
          ...(PrivateSchoolsOptions.length
            ? [{ options: PrivateSchoolsOptions }]
            : []),
          ...(OtherPublicOptions.length
            ? [{ options: OtherPublicOptions }]
            : []),
          ...(specialeducationOptions.length
            ? [{ options: specialeducationOptions }]
            : []),
          ...(teacherTrainingOptions.length
            ? [{ link: "/teachertraining" }]
            : []),
          ...(CollegePublicPrivateOptions.length
            ? [{ options: CollegePublicPrivateOptions }]
            : []),
          ...(DeeniMadarisOptions.length
            ? [{ options: DeeniMadarisOptions }]
            : []),
        ];

        for (const item of menuItemsConfig) {
          if (item.link) return item.link;
          if (item.options?.length) return item.options[0].link;
        }
        return "/";
      };

      setFirstPermittedRoute(getFirstSidebarRoute());
    }
  }, [authUser?.user_permissions]);

  const onSubmit = async (form) => {
    try {
      const userData = {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        isWeb: true,
        platform: "web",
      };

      const result = await dispatch(login(userData)).unwrap();
      const { token, data, message } = result;

      if (token) localStorage.setItem("token", token);
      toast.success(message || "Login successful");

      // the user came from the GIS site and logs in to get back to it
      if (sessionStorage.getItem("returnToGis")) {
        navigate("/gis-launch", { replace: true });
        return;
      }

      const lastVisited = sessionStorage.getItem("lastVisitedPath");
      if (lastVisited && lastVisited !== "/login") {
        navigate(lastVisited);
        return;
      }

      const permissions = data?.user_permissions || [];
      if (permissions.includes("group-view")) navigate("/");
      else if (permissions.includes("public-private-colleges-detailed-view"))
        navigate("/collegePublicPrivateDetailed");
      else if (permissions.includes("Non-Formal-Detail-get"))
        navigate("/non_formal_detailed");
      else if (permissions.includes("special-education-detail-fetch"))
        navigate("/special_detailed");
      else if (permissions.includes("education-foundation-view"))
        navigate("/ef_detailed");
      else if (permissions.includes("deeni-madaris-summarized-view"))
        navigate("/deeni_madaris_summarized");
      else if (permissions.includes("teacherTraining"))
        navigate("/teachertraining");
      else if (permissions.includes("private-school-summarized-view"))
        navigate("/ps_summarized");
      else if (permissions.includes("other-public-fetch"))
        navigate("/other_summarized");
      else navigate("/");
    } catch (error) {
      console.log("Type:", typeof error);
      console.log("Full error:", JSON.stringify(error));
      toast.error(error?.message || "Login failed");
    }
  };

  const togglePasswordVisibility = () => setShowPassword((p) => !p);

  return (
    <div
      className="bg-cover bg-center h-screen w-full relative overflow-hidden"
      style={{ backgroundImage: `url(${bg})` }}
    >
      <div className="h-full flex justify-end items-center">
        <div className="lg:w-[64.66666667%] w-full h-full lg:px-0 px-4 sm:px-10">
          <div className="h-full py-4 flex lg:justify-end justify-center items-center lg:mr-[11.8rem] mr-0">
            <div className="w-full lg:w-[62.666%] lg:min-w-[460px] rounded-lg border-0 bg-white shadow-sm relative pb-[clamp(10px,3vh,28px)]">
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="w-full gradiant px-[23px] py-[13px] rounded-t-lg">
                  <div className="flex justify-between items-center">
                    <p className="text-white text-[0.875rem]">SIGN IN</p>
                  </div>
                </div>

                <div className="flex flex-col justify-center items-center mt-[clamp(8px,3.2vh,32px)] w-full lg:px-[2.3rem] px-[1rem]">

                  <div className="flex items-center justify-center gap-5 sm:gap-7 w-full">
                    <img src={unido} alt="UNIDO" className="h-[clamp(34px,6.5vh,58px)] w-auto shrink-0" />
                    <img src={logo} alt="Government of Sindh" className="h-[clamp(38px,7vh,64px)] aspect-[95/97] object-cover object-left shrink-0" />
                    <img src={logo} alt="SPHF" className="h-[clamp(38px,7vh,64px)] aspect-[233/97] object-cover object-right shrink-0" />
                    <div className="relative h-[clamp(27px,5.1vh,45px)] aspect-[1128/803] overflow-hidden shrink-0">
                      <img src={uno} alt="European Union" className="absolute max-w-none w-[136.17%] left-[-18.09%] top-[-13.45%]" />
                    </div>
                  </div>

                  <h1 className="mt-[clamp(8px,3.2vh,32px)] md:text-[clamp(18px,2.7vh,24px)] leading-tight text-[18px] text-[#212529] text-center font-semibold text-balance">
                    Climate Resilient Settlement Development Plan (CRSDP)
                    Management Dashboard
                  </h1>

                  <p className="mt-[clamp(4px,1.2vh,12px)] md:text-[13px] text-[12px] leading-5 font-normal text-[#6c757d] text-center text-balance">
                    Developed under the Poverty Alleviation &amp; Inclusive
                    Development Across Rural Sindh (PAIDAR) programme
                  </p>

                  <img src={logo2} alt="PAIDAR" className="w-[130px] mt-[clamp(8px,3.2vh,32px)] mb-[clamp(8px,3.2vh,32px)]" />

                  {/* Email address (no spaces) */}
                  <ThinTextField
                    id="email"
                    name="email"
                    type="email"
                    value={emailVal}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    onKeyDown={handleKeyDown}
                    isRequired={false}
                    className="w-full"
                    label="Email address"
                    placeHolder="Email address"
                    placeholder="Email address"
                    autoComplete="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />

                  {/* Password (no spaces) */}
                  <div className="relative w-full mt-[clamp(8px,2vh,20px)]">
                    <ThinTextField
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={passwordVal}
                      onChange={(e) =>
                        handleInputChange("password", e.target.value)
                      }
                      onKeyDown={handleKeyDown}
                      isRequired={false}
                      label="Password"
                      placeHolder="Password"
                      placeholder="Password"
                      className="w-full"
                      autoComplete="current-password"
                      error={!!errors.password}
                      helperText={errors.password?.message}
                    />
                    <div
                      className="absolute right-3 top-9 transform -translate-y-1/2 cursor-pointer text-gray-500 mt-2"
                      onClick={togglePasswordVisibility}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <FaEye /> : <FaEyeSlash />}
                    </div>
                  </div>

                  <div className="flex mt-[clamp(10px,2.6vh,28px)] items-center justify-between gap-3 w-full">
                    <div className="flex items-center">
                      <Checkbox size="small" />
                      <p className="lg:text-[16px] text-[12px] font-[Regular] text-[#212529] cursor-pointer">
                        Remember me?
                      </p>
                    </div>
                    <div className="[&>div]:mt-0">
                      <SubmitButton
                        icon={<i className="fas fa-sign-in-alt text-[#70c6c9]"></i>}
                        type="submit"
                        text={loading ? "Loading..." : "Sign In"}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <p className="text-white ml-10 absolute bottom-4 text-[14px] [text-shadow:0_1px_3px_rgba(0,0,0,0.6)]">
        Copyright © {new Date().getFullYear()}, Sindh - CRSDP
      </p>
    </div>
  );
};

export default Login;
